import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    useTheme,
    CircularProgress,
    Snackbar,
    Alert,
    Switch,
    FormControlLabel,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { collectionPointsService, CollectionPoint, CollectionPointDPOptionMap } from '../../services/collectionpoints-service';
import { dpOptionsService, DPOption } from '../../services/dpoptions-service';
import { config } from '../../config/config';

interface PreferencesTabProps {
    collectionPoint: CollectionPoint | null;
    isPublished: boolean;
    onPreferencesChanged?: () => void; // Optional callback to notify parent of changes
}

const PreferencesTab: React.FC<PreferencesTabProps> = ({ collectionPoint, isPublished, onPreferencesChanged }) => {
    const theme = useTheme();
    
    // Preferences-related state
    const [dpOptionMappings, setDPOptionMappings] = useState<CollectionPointDPOptionMap[]>([]);
    const [loadingDPOptions, setLoadingDPOptions] = useState(false);
    const [availableDPOptions, setAvailableDPOptions] = useState<DPOption[]>([]);
    
    // Loading and error state for remove operations
    const [removingDPOptionId, setRemovingDPOptionId] = useState<number | null>(null);
    const [mappingDPOptionId, setMappingDPOptionId] = useState<number | null>(null);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'warning' | 'info';
    }>({
        open: false,
        message: '',
        severity: 'info'
    });
    
    // State for tracking "isRequired" values for each preference
    const [requiredStates, setRequiredStates] = useState<Record<number, boolean>>({});
    
    // Preferences-related useEffect hooks
    useEffect(() => {
        if (collectionPoint?.CollectionPointId) {
            setLoadingDPOptions(true);
            collectionPointsService.getMappedDPOptions(collectionPoint.CollectionPointId)
                .then(mappings => {
                    setDPOptionMappings(mappings);
                    setLoadingDPOptions(false);
                });
        }
    }, [collectionPoint]);

    useEffect(() => {
        if (!isPublished) {
            dpOptionsService.getDPOptions({
                filters: { isPublished: true }, // Only get published DP options
                pagination: { page: 1, pageSize: 1000 }, // Large page size to get all published options
                sortBy: 'A-Z'
            })
                .then(options => {
                    const mappedOptionIds = dpOptionMappings.map(m => m.DPOptionId);
                    setAvailableDPOptions(options.data.filter(o => !mappedOptionIds.includes(o.id)));
                })
                .catch(error => {
                    console.error('Error loading available DP options:', error);
                });
        }
    }, [collectionPoint, dpOptionMappings, isPublished]);
    
    // Handle required toggle for preferences
    const handleRequiredToggle = (dpOptionId: number, isRequired: boolean) => {
        setRequiredStates(prev => ({
            ...prev,
            [dpOptionId]: isRequired
        }));
    };

    // Preferences-related handler functions
    const handleMapDPOption = async (dpOptionId: number) => {
        if (!collectionPoint?.CollectionPointId) return;
        try {
            setMappingDPOptionId(dpOptionId);
            
            // Get the isRequired value for this preference (default to true if not set)
            const isRequired = requiredStates[dpOptionId] !== undefined ? requiredStates[dpOptionId] : true;
            
            // Map the DP option to the collection point
            await collectionPointsService.mapDPOption(collectionPoint.CollectionPointId, dpOptionId, isRequired);
            
            // Fetch fresh data from server after successful mapping
            const freshMappings = await collectionPointsService.getMappedDPOptions(collectionPoint.CollectionPointId);
            setDPOptionMappings(freshMappings);
            
            if (onPreferencesChanged) {
                onPreferencesChanged();
            }
            
            // Show success message
            setSnackbar({
                open: true,
                message: 'Preference added successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error mapping DP option:', error);
            // Show error message
            setSnackbar({
                open: true,
                message: error instanceof Error ? error.message : 'Failed to add preference',
                severity: 'error'
            });
        } finally {
            setMappingDPOptionId(null);
        }
    };

    const handleUnmapDPOption = async (dpOptionId: number) => {
        if (!collectionPoint?.CollectionPointId) return;
        
        setRemovingDPOptionId(dpOptionId);
        try {
            // Unmap the DP option from the collection point
            await collectionPointsService.unmapDPOption(collectionPoint.CollectionPointId, dpOptionId);
            
            // Fetch fresh data from server after successful unmapping
            const freshMappings = await collectionPointsService.getMappedDPOptions(collectionPoint.CollectionPointId);
            setDPOptionMappings(freshMappings);
            
            if (onPreferencesChanged) {
                onPreferencesChanged();
            }
            
            // Show success message
            setSnackbar({
                open: true,
                message: 'Preference removed successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error unmapping DP option:', error);
            // Show error message
            setSnackbar({
                open: true,
                message: error instanceof Error ? error.message : 'Failed to remove preference',
                severity: 'error'
            });
        } finally {
            setRemovingDPOptionId(null);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };
    
    return (
        <Box sx={{ p: 3 }}>
            {loadingDPOptions ? (
                <Typography color="text.primary">Loading DP options...</Typography>
            ) : (
                <>
                    <Typography variant="subtitle1" sx={{ mb: 2, color: theme.palette.text.primary }}>
                        Mapped DP Options
                    </Typography>
                    {dpOptionMappings.length === 0 ? (
                        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No DP options mapped to this collection point.
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {dpOptionMappings.map((mapping) => (
                                <Box key={mapping.MappingId} sx={{
                                    minWidth: 320,
                                    maxWidth: 400,
                                    flex: '1 1 320px',
                                    bgcolor: theme.palette.background.paper,
                                    borderRadius: theme.shape.borderRadius,
                                    boxShadow: theme.shadows[2],
                                    p: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                    border: `1px solid ${theme.palette.divider}`,
                                    position: 'relative',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="h6" color="text.primary" sx={{ flex: 1 }}>{mapping.Name}</Typography>
                                        {!isPublished && (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={() => handleUnmapDPOption(mapping.DPOptionId)}
                                                disabled={removingDPOptionId === mapping.DPOptionId}
                                                sx={{
                                                    borderColor: theme.palette.error.main,
                                                    color: theme.palette.error.main,
                                                    minWidth: 80,
                                                    '&:hover': {
                                                        borderColor: theme.palette.error.dark,
                                                        bgcolor: theme.palette.error.light,
                                                    },
                                                }}
                                            >
                                                {removingDPOptionId === mapping.DPOptionId ? (
                                                    <CircularProgress size={16} color="error" />
                                                ) : (
                                                    'Remove'
                                                )}
                                            </Button>
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {mapping.Description}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Display As: {mapping.DisplayAs}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Multiple Selection: {mapping.AllowMultipleSelection ? 'Yes' : 'No'}
                                        </Typography>
                                        <Chip
                                            icon={mapping.IsMandatory ? <StarIcon /> : <StarBorderIcon />}
                                            label={mapping.IsMandatory ? 'Required' : 'Optional'}
                                            size="small"
                                            variant={mapping.IsMandatory ? 'filled' : 'outlined'}
                                            sx={{
                                                fontSize: '0.7rem',
                                                height: 24,
                                                '& .MuiChip-icon': {
                                                    fontSize: '1rem',
                                                },
                                                ...(mapping.IsMandatory ? {
                                                    bgcolor: theme.palette.error.main,
                                                    color: theme.palette.error.contrastText,
                                                    '&:hover': {
                                                        bgcolor: theme.palette.error.dark,
                                                    },
                                                } : {
                                                    bgcolor: 'transparent',
                                                    color: theme.palette.text.secondary,
                                                    borderColor: theme.palette.divider,
                                                    '&:hover': {
                                                        bgcolor: theme.palette.action.hover,
                                                    },
                                                }),
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                            Options:
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {mapping.Options.map((opt, index) => (
                                                <Chip
                                                    key={index}
                                                    label={opt.value}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: theme.palette.background.default,
                                                        color: theme.palette.text.primary,
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {!isPublished && (
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="subtitle1" sx={{ mb: 2, color: theme.palette.text.primary }}>
                                Available DP Options
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {availableDPOptions.map((option) => (
                                    <Box key={option.id} sx={{
                                        minWidth: 320,
                                        maxWidth: 400,
                                        flex: '1 1 320px',
                                        bgcolor: theme.palette.background.paper,
                                        borderRadius: theme.shape.borderRadius,
                                        boxShadow: theme.shadows[2],
                                        p: 3,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1.5,
                                        border: `1px solid ${theme.palette.divider}`,
                                        position: 'relative',
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="h6" color="text.primary" sx={{ flex: 1 }}>{option.name}</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={requiredStates[option.id] !== undefined ? requiredStates[option.id] : true}
                                                            onChange={(e) => handleRequiredToggle(option.id, e.target.checked)}
                                                            size="small"
                                                            disabled={mappingDPOptionId === option.id}
                                                            sx={{
                                                                '& .MuiSwitch-switchBase': {
                                                                    color: theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[300],
                                                                },
                                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                                    color: theme.palette.primary.main,
                                                                },
                                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                    backgroundColor: theme.palette.primary.main,
                                                                },
                                                                '& .MuiSwitch-track': {
                                                                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
                                                                },
                                                                '& .MuiSwitch-switchBase.Mui-disabled': {
                                                                    color: theme.palette.action.disabled,
                                                                },
                                                                '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                                                                    backgroundColor: theme.palette.action.disabledBackground,
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography 
                                                            variant="caption" 
                                                            sx={{ 
                                                                fontSize: '0.75rem',
                                                                color: theme.palette.text.secondary
                                                            }}
                                                        >
                                                            Required
                                                        </Typography>
                                                    }
                                                    sx={{ mr: 0 }}
                                                />
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    disabled={mappingDPOptionId === option.id}
                                                    onClick={() => handleMapDPOption(option.id)}
                                                    sx={{
                                                        bgcolor: theme.palette.primary.main,
                                                        color: theme.palette.primary.contrastText,
                                                        '&:hover': {
                                                            bgcolor: theme.palette.primary.dark,
                                                        },
                                                        '&:disabled': {
                                                            bgcolor: theme.palette.action.disabledBackground,
                                                            color: theme.palette.action.disabled,
                                                        },
                                                    }}
                                                >
                                                    {mappingDPOptionId === option.id ? (
                                                        <CircularProgress size={16} color="inherit" />
                                                    ) : (
                                                        'Add'
                                                    )}
                                                </Button>
                                            </Box>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            {option.description}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Display As: {option.displayAs}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Multiple Selection: {option.allowMultipleSelection ? 'Yes' : 'No'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                Options:
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {option.options.map((opt, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={opt.optionValue}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: theme.palette.background.default,
                                                            color: theme.palette.text.primary,
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </>
            )}
            
            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={config.ui.snackbar.autoHideDuration}
                onClose={handleCloseSnackbar}
                anchorOrigin={config.ui.snackbar.anchorOrigin}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PreferencesTab; 